<template>
  <!-- Tooltip with Requirements Checklist - Wrapping entire component -->
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger as-child>
        <div class="my-3 py-1 px-2 bg-gray-50 rounded border max-w-sm cursor-pointer hover:bg-gray-100 transition-colors">
          <!-- Password Strength Bar -->
          <div class="space-y-1">
            <div class="w-full bg-gray-200 rounded-full h-1">
              <div
                class="h-1 rounded-full transition-all duration-300 ease-in-out"
                :class="[strength.color, { 'animate-pulse': isLoading }]"
                :style="{ width: `${strength.percentage}%` }"
              ></div>
            </div>
            <div class="flex justify-between items-center gap-y-0">
              <div class="flex items-center gap-2">
                <span class="text-xs font-semibold" :class="strengthTextColor">{{ strength.label }}</span>
                <!-- Loading indicator -->
                <svg 
                  v-if="isLoading" 
                  class="w-3 h-3 text-gray-400 animate-spin" 
                  fill="none" 
                  viewBox="0 0 24 24"
                >
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <!-- Error indicator -->
                <svg 
                  v-else-if="error" 
                  class="w-3 h-3 text-red-400" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                  :title="error"
                >
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                </svg>
              </div>

              <!-- Info icon hint -->
              <svg class="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
        </div>
      </TooltipTrigger>
            <TooltipContent side="top" class="max-w-xs">
              <div class="space-y-2">
                <h4 class="text-xs font-medium text-gray-900 mb-2">{{ $t('passwordValidation.requirements.title') }}</h4>
                
                <!-- Server-side validation errors (if any) -->
                <div v-if="validationResult?.errors?.length" class="mb-2 p-2 bg-red-50 rounded text-xs">
                  <div v-for="error in validationResult.errors" :key="error" class="text-red-700">
                    • {{ error }}
                  </div>
                </div>

                <!-- Server-side suggestions (if any) -->
                <div v-if="validationResult?.suggestions?.length" class="mb-2 p-2 bg-blue-50 rounded text-xs">
                  <div class="text-blue-700 font-medium mb-1">Suggestions:</div>
                  <div v-for="suggestion in validationResult.suggestions" :key="suggestion" class="text-blue-600">
                    • {{ suggestion }}
                  </div>
                </div>

                <!-- Requirements checklist -->
                <div
                  v-for="requirement in requirements"
                  :key="requirement.key"
                  class="flex items-center text-xs"
                  :class="requirement.satisfied ? 'text-green-600' : 'text-gray-600'"
                >
                  <!-- Check/X Icon -->
                  <svg
                    v-if="requirement.satisfied"
                    class="w-3 h-3 mr-2 text-green-500 shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <svg v-else class="w-3 h-3 mr-2 text-gray-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span>{{ requirement.label }}</span>
                </div>

                <!-- Advanced feedback from server (if available) -->
                <div v-if="strength.feedback?.warning" class="mt-2 p-2 bg-yellow-50 rounded text-xs">
                  <div class="text-yellow-700 font-medium">⚠️ {{ strength.feedback.warning }}</div>
                </div>

                <!-- Password strength details -->
                <div v-if="validationResult" class="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-600">
                  <div>Length: {{ validationResult.length }} characters</div>
                  <div v-if="strength.crack_time_display">
                    Crack time: {{ strength.crack_time_display }}
                  </div>
                  <div>Score: {{ strength.score }}/4</div>
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
</template>

<script setup lang="ts">
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '~/components/ui/tooltip';
import { Button } from '~/components/ui/button';

interface Props {
  password: string;
}

const props = defineProps<Props>();
const { t } = useI18n();

// Use the unified password validation composable
const passwordRef = toRef(props, 'password');
const validation = usePasswordValidation(passwordRef);

// Extract validation data
const requirements = computed(() => validation.requirements.value);
const strength = computed(() => validation.strength.value);
const isValid = computed(() => validation.isValid.value);
const validationResult = computed(() => validation.validationResult.value);
const isLoading = computed(() => validation.isLoading.value);
const error = computed(() => validation.error.value);

const strengthTextColor = computed(() => {
  // Use score-based colors for consistent styling
  const score = strength.value.score;
  switch (score) {
    case 4:
      return 'text-green-600';
    case 3:
      return 'text-yellow-600';
    case 2:
      return 'text-orange-600';
    case 1:
      return 'text-red-400';
    case 0:
    default:
      return 'text-red-600';
  }
});

// Expose validation state for parent components
defineExpose({
  isValid,
  strength,
  requirements,
  validationResult,
  isLoading,
  error,
});
</script>
